/**
 * TerrainMaterialManager — stabile Shader-Varianten fuer streamende Kacheln.
 *
 * Three.js teilt WebGLPrograms bereits nach Program-Key; verschiedene
 * Materialinstanzen erzeugen daher nicht automatisch verschiedene Shader.
 * Ein Wechsel `map: null -> Texture` aendert aber USE_MAP und erzwingt einen
 * neuen Program-Key mitten in der Kamerafahrt. Diese Factory bindet deshalb
 * immer eine 1x1-Platzhaltertextur und haelt genau zwei Templates am Leben:
 * normale Innenkachel und Regionsrand mit AlphaMap/AlphaTest.
 */
import * as THREE from 'three';

const FALLBACK_COLOR = 0x4a5d4a;
const ALPHA_TEST = 0.45;

export class TerrainMaterialManager {
    private readonly _mapPlaceholder: THREE.DataTexture;
    private readonly _alphaPlaceholder: THREE.DataTexture;
    private readonly _interiorTemplate: THREE.MeshStandardMaterial;
    private readonly _edgeTemplate: THREE.MeshStandardMaterial;
    private _disposed = false;

    constructor() {
        this._mapPlaceholder = this._makePixelTexture([255, 255, 255, 255], THREE.SRGBColorSpace);
        this._mapPlaceholder.name = 'terrain/map-placeholder';
        this._alphaPlaceholder = this._makePixelTexture([255, 255, 255, 255], THREE.NoColorSpace);
        this._alphaPlaceholder.name = 'terrain/alpha-placeholder';

        this._interiorTemplate = new THREE.MeshStandardMaterial({
            map: this._mapPlaceholder,
            color: FALLBACK_COLOR,
            roughness: 1,
            metalness: 0,
        });
        this._interiorTemplate.defines = { TERRAIN_TILE: 1 };
        this._interiorTemplate.name = 'terrain/material-interior-template';

        this._edgeTemplate = new THREE.MeshStandardMaterial({
            map: this._mapPlaceholder,
            color: FALLBACK_COLOR,
            roughness: 1,
            metalness: 0,
            alphaMap: this._alphaPlaceholder,
            alphaTest: ALPHA_TEST,
        });
        this._edgeTemplate.defines = { TERRAIN_TILE: 1 };
        this._edgeTemplate.name = 'terrain/material-edge-template';
    }

    /** Eine eigene Uniform-Huelle je Kachel, aber ein stabiler Program-Key. */
    createMaterial(map: THREE.Texture | null, alphaMap: THREE.Texture | null): THREE.MeshStandardMaterial {
        const material = (alphaMap ? this._edgeTemplate : this._interiorTemplate).clone();
        const hasRealMap = map !== null && map !== this._mapPlaceholder;
        material.map = map ?? this._mapPlaceholder;
        material.color.set(hasRealMap ? 0xffffff : FALLBACK_COLOR);
        if (alphaMap) material.alphaMap = alphaMap;
        return material;
    }

    /**
     * Tauscht nur die Map-Uniform aus. Weil vorher und nachher eine Texture
     * gebunden ist, bleibt USE_MAP aktiv; `needsUpdate` waere hier schaedlich
     * und ist bewusst nicht gesetzt.
     */
    replaceMap(material: THREE.MeshStandardMaterial, map: THREE.Texture): void {
        material.map = map;
        material.color.set(0xffffff);
    }

    isPlaceholderMap(texture: THREE.Texture | null): boolean {
        return texture === this._mapPlaceholder;
    }

    disposeMaterial(material: THREE.MeshStandardMaterial, disposeMap: boolean): void {
        if (disposeMap && material.map && material.map !== this._mapPlaceholder) material.map.dispose();
        if (material.alphaMap && material.alphaMap !== this._alphaPlaceholder) material.alphaMap.dispose();
        material.dispose();
    }

    /**
     * Kompiliert beide Terrain-Varianten vor dem ersten Kameraflug. Three.js
     * nutzt in compileAsync automatisch KHR_parallel_shader_compile, sofern
     * der Browser die Extension anbietet. Der anschliessende 2x1-Offscreen-
     * Draw zieht die absichtlich lazy Program-Diagnostik (InfoLog/Uniforms)
     * ebenfalls in die Ladephase statt in die Kamerafahrt.
     */
    async warmupShaders(
        renderer: THREE.WebGLRenderer,
        targetScene: THREE.Scene,
        camera: THREE.Camera,
    ): Promise<void> {
        if (this._disposed || renderer.getContext().isContextLost()) return;

        const geometry = new THREE.PlaneGeometry(0.8, 0.8);
        const interior = new THREE.Mesh(geometry, this._interiorTemplate);
        const edge = new THREE.Mesh(geometry, this._edgeTemplate);
        interior.position.x = -0.5;
        edge.position.x = 0.5;
        interior.frustumCulled = edge.frustumCulled = false;
        const warmupGroup = new THREE.Group();
        warmupGroup.name = 'terrain/shader-warmup';
        warmupGroup.add(interior, edge);

        /* Exakte Laufzeit-Szene statt nachgebauter Lichter: Schon eine
         * abweichende Light-/Environment-Konfiguration erzeugt einen anderen
         * Three.js-Program-Key. Die Planes sitzen direkt vor der echten
         * Kamera, sodass der Offscreen-Draw genau die spaeteren Terrain-
         * Programme bis einschliesslich InfoLog/Uniform-Lookup erstbenutzt. */
        camera.updateMatrixWorld(true);
        const forward = new THREE.Vector3();
        camera.getWorldDirection(forward);
        warmupGroup.position.copy(camera.getWorldPosition(new THREE.Vector3())).addScaledVector(forward, 2);
        warmupGroup.quaternion.copy(camera.getWorldQuaternion(new THREE.Quaternion()));
        targetScene.add(warmupGroup);

        const target = new THREE.WebGLRenderTarget(2, 1, {
            depthBuffer: true,
            stencilBuffer: false,
        });
        const previousTarget = renderer.getRenderTarget();
        const previousAutoClear = renderer.autoClear;
        try {
            await renderer.compileAsync(targetScene, camera);
            renderer.setRenderTarget(target);
            renderer.autoClear = true;
            renderer.clear();
            renderer.render(targetScene, camera);
        } finally {
            targetScene.remove(warmupGroup);
            renderer.setRenderTarget(previousTarget);
            renderer.autoClear = previousAutoClear;
            target.dispose();
            geometry.dispose();
        }
    }

    dispose(): void {
        if (this._disposed) return;
        this._disposed = true;
        this._interiorTemplate.dispose();
        this._edgeTemplate.dispose();
        this._mapPlaceholder.dispose();
        this._alphaPlaceholder.dispose();
    }

    private _makePixelTexture(
        rgba: [number, number, number, number],
        colorSpace: THREE.ColorSpace,
    ): THREE.DataTexture {
        const texture = new THREE.DataTexture(new Uint8Array(rgba), 1, 1, THREE.RGBAFormat);
        texture.colorSpace = colorSpace;
        texture.minFilter = THREE.NearestFilter;
        texture.magFilter = THREE.NearestFilter;
        texture.generateMipmaps = false;
        texture.needsUpdate = true;
        return texture;
    }
}
